import { Combobox, Dialog, Transition } from '@headlessui/react'
import { Button } from '@material-tailwind/react'
import { Fragment, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { P } from '../../../components/typography/typography'
import dentistService, { Dentist } from '../../../Services/dentist.services'

// Every dentist can match a short query, so the picker only lists the first few
const maxMatches = 20

const dentistName = (dentist: Dentist) => `${dentist.firstName ?? ''} ${dentist.lastName ?? ''}`.trim()

const PracticeDentistsDialog = (props: any) => {
  const [dentists, setDentists] = useState<Dentist[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [savingId, setSavingId] = useState<number>()
  const [errorMessage, setErrorMessage] = useState('')

  const practiceId: number | undefined = props.data?.id

  useEffect(() => {
    if (props.isOpen) {
      getDentists()
    }
  }, [props.isOpen, practiceId])

  const getDentists = async () => {
    setIsLoading(true)
    const result = await dentistService.GetDentists()
    if (!result.success) {
      setErrorMessage('Could not load dentists. Please try again later.')
    }
    setDentists(result.data)
    setIsLoading(false)
  }

  function closeModal() {
    props.stateManager()
    setQuery('')
    setErrorMessage('')
  }

  const linked = dentists.filter((x) => x.practiceId === practiceId)

  const search = query.trim().toLowerCase()
  const matches = search
    ? dentists
        .filter((x) => x.practiceId !== practiceId)
        .filter((x) => dentistName(x).toLowerCase().includes(search) || (x.email ?? '').toLowerCase().includes(search))
        .slice(0, maxMatches)
    : []

  const setPractice = async (dentist: Dentist, newPracticeId: number | null) => {
    setErrorMessage('')
    setSavingId(dentist.id)
    try {
      const updated = await dentistService.SetDentistPractice(dentist.id, newPracticeId)
      setDentists((current) => current.map((x) => (x.id === updated.id ? updated : x)))
      toast.success(newPracticeId ? 'Dentist added to the practice' : 'Dentist removed from the practice', {
        position: 'top-center',
      })
      // Refresh the row's dentist count
      props.GetPractices()
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.error ?? 'Something went wrong')
    } finally {
      setSavingId(undefined)
    }
  }

  const addDentist = (dentist: Dentist | null) => {
    if (!dentist || practiceId === undefined) {
      return
    }
    setQuery('')
    setPractice(dentist, practiceId)
  }

  return (
    <div>
      <Transition appear show={props.isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h1" className="font-medium leading-tight text-2xl mt-0 mb-2 text-black-600">
                    Dentists in {props.data?.name}
                  </Dialog.Title>

                  {isLoading ? (
                    <p>Loading...</p>
                  ) : (
                    <div className="relative py-4 flex-auto">
                      {linked.length === 0 ? (
                        <p className="mb-4 text-slate-500">No dentists are linked to this practice.</p>
                      ) : (
                        <ul className="mb-4 divide-y divide-slate-200">
                          {linked.map((dentist) => (
                            <li key={dentist.id} className="flex items-center justify-between py-2">
                              <div>
                                <p>{dentistName(dentist)}</p>
                                <p className="text-sm text-slate-500">{dentist.email}</p>
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                color="red"
                                variant="outlined"
                                disabled={savingId !== undefined}
                                onClick={() => setPractice(dentist, null)}
                              >
                                Remove
                              </Button>
                            </li>
                          ))}
                        </ul>
                      )}

                      <Combobox value={null} onChange={addDentist} disabled={savingId !== undefined} nullable>
                        <Combobox.Label className="block mb-1 font-medium">Add dentist</Combobox.Label>
                        <Combobox.Input
                          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                          placeholder="Search by name or email"
                          displayValue={() => query}
                          onChange={(event) => setQuery(event.target.value)}
                        />
                        {search && (
                          <Combobox.Options className="mt-1 max-h-60 overflow-auto rounded-md border border-slate-200 text-sm">
                            {matches.length === 0 ? (
                              <p className="px-3 py-2 text-slate-500">No dentists match.</p>
                            ) : (
                              matches.map((dentist) => (
                                <Combobox.Option
                                  key={dentist.id}
                                  value={dentist}
                                  className={({ active }) => `cursor-pointer px-3 py-2 ${active ? 'bg-blue-50' : ''}`}
                                >
                                  <p>
                                    {dentistName(dentist)} <span className="text-slate-500">{dentist.email}</span>
                                  </p>
                                  {/* A dentist belongs to one practice at most, so adding them here moves them */}
                                  {dentist.practiceName && (
                                    <p className="text-xs text-orange-700">
                                      In {dentist.practiceName}. Adding them moves them to this practice.
                                    </p>
                                  )}
                                </Combobox.Option>
                              ))
                            )}
                          </Combobox.Options>
                        )}
                      </Combobox>
                    </div>
                  )}

                  {errorMessage && <P className="text-red-600">{errorMessage}</P>}

                  <div className="flex items-center justify-end pt-4 border-t border-solid border-slate-200">
                    <Button type="button" onClick={closeModal}>
                      Done
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}

export default PracticeDentistsDialog
